import React, { useEffect } from 'react'
import { store } from '@redux/store'
import { Provider } from 'react-redux'
import Modules from '@components/Modules'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { useLocation } from '@reach/router'
import { GlobalDataProvider } from '@context/GlobalDataContext'
import fontMap from './generated/font-map.json'

export const wrapRootElement = ({ element }: any) => {
  return <Provider store={store}>{element}</Provider>
}

const NavigationHandler = () => {
  const location = useLocation()
  const data = useStaticQuery(graphql`
    query GlobalStaticData {
      siteCompilerData {
        prehome_components {
          type_id
        }
        divisions {
          path
        }
      }
    }
  `)

  const isSiteCompiler = process.env.GATSBY_IS_SITE_COMPILER === 'true'
  const useHomeFirstDivision =
    isSiteCompiler && data?.siteCompilerData?.prehome_components?.length === 0
  const firstDivisionPath = isSiteCompiler && data?.siteCompilerData?.divisions[0]?.path

  useEffect(() => {
    if (location.pathname === '/' && useHomeFirstDivision && firstDivisionPath) {
      navigate(firstDivisionPath)
    }
  }, [useHomeFirstDivision, firstDivisionPath, location.pathname])

  return null
}
export const wrapPageElement = ({ element, props }: any) => {
  const {
    pageContext: { global_data, hidden_header, hidden_footer, divisions, three },
    development_id,
    property_id,
    novelty_id,
  } = props

  const {
    content: { header, pages, footer },
    pageConfig: {
      design: { h1, h2, h3, h4, h5, p, p_bold, a, global },
    },
  } = global_data

  let Header
  try {
    Header = require(`./components/header/${header.type_id}`).default
  } catch (e) {
    return <></>
  }

  let Footer
  try {
    Footer = require(`./components/footer/${footer.type_id}`).default
  } catch (e) {
    return <></>
  }

  return (
    <GlobalDataProvider global_data={global_data}>
      <style type="text/css">{`
        @font-face {
          font-family: h1CustomFont;
          src: url(${fontMap[h1.file as keyof typeof fontMap] || h1.file});
          font-display: swap;
        }
        @font-face {
          font-family: h2CustomFont;
          src: url(${fontMap[h2.file as keyof typeof fontMap] || h2.file});
          font-display: swap;
        }
        @font-face {
          font-family: h3CustomFont;
          src: url(${fontMap[h3.file as keyof typeof fontMap] || h3.file});
          font-display: swap;
        }
        @font-face {
          font-family: h4CustomFont;
          src: url(${fontMap[h4.file as keyof typeof fontMap] || h4.file});
          font-display: swap;
        }
        @font-face {
          font-family: h5CustomFont;
          src: url(${fontMap[h5.file as keyof typeof fontMap] || h5.file});
          font-display: swap;
        }
        @font-face {
          font-family: pCustomFont;
          src: url(${fontMap[p.file as keyof typeof fontMap] || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: pBoldCustomFont;
          font-weight:500;
          src: url(${fontMap[(p_bold?.file || p.file) as keyof typeof fontMap] || p_bold?.file || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: aCustomFont;
          src: url(${fontMap[(a?.file || p.file) as keyof typeof fontMap] || a?.file || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: globalCustomFont;
          src: url(${fontMap[global.file as keyof typeof fontMap] || global.file});
          font-display: swap;
        }
      `}</style>
      <div suppressHydrationWarning>
        <NavigationHandler />
        <Modules
          development_id={development_id}
          property_id={property_id}
          novelty_id={novelty_id}
          pages={pages}
          global_data={global_data}
        />
        {!hidden_header && (
          <Header
            development_id={development_id}
            property_id={property_id}
            novelty_id={novelty_id}
            three={three}
            {...{ type_id: header.type_id, pages, global_data, divisions, ...header.data }}
          />
        )}
        {element}
        {!hidden_footer && (
          <Footer
            {...{
              type_id: footer.type_id,
              data: footer.data,
              ...footer.data,
              pages,
              global_data,
              development_id,
              property_id,
              novelty_id,
            }}
          />
        )}
      </div>
    </GlobalDataProvider>
  )
}
